/**
 * OvertimeSettingsTab
 * Allows org admins to configure overtime tracking:
 *  - Master enable/disable toggle
 *  - Work type CRUD (Time & a Half, Double Time, etc.)
 *
 * All CRUD operations are wired to the OvertimeTypes API.
 */

import { useState } from 'react';
import { Plus, Pencil, PowerOff, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { OvertimeSettings, OvertimeType } from '@/types/timesheetSettings';
import { SEEDED_OVERTIME_TYPES } from '@/data/mockOvertimeData';
import {
  createOvertimeType,
  updateOvertimeType,
  deleteOvertimeType,
  setDefaultOvertimeType,
} from '@/services/api/overtimeTypesApi';
import type { CreateOvertimeTypePayload, UpdateOvertimeTypePayload } from '@/services/api/overtimeTypesApi';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────────────────────

const BADGE_COLOURS = [
  { hex: 'var(--purple-500)',        label: 'Purple' },
  { hex: 'var(--sky-500)',           label: 'Sky' },
  { hex: 'var(--cyan-500)',          label: 'Cyan' },
  { hex: 'var(--sapgreen-500)',      label: 'Sap Green' },
  { hex: 'var(--burntorange-400)',   label: 'Burnt Orange' },
  { hex: 'var(--brickred-500)',      label: 'Brick Red' },
  { hex: 'var(--electricviolet-500)', label: 'Electric Violet' },
  { hex: 'var(--blueviolet-500)',    label: 'Blue Violet' },
];

const BLANK_TYPE: Omit<OvertimeType, 'id' | 'createdAt'> = {
  name: '',
  costMultiplier: undefined,
  billMultiplier: undefined,
  badgeColour: 'var(--purple-500)',
  isDefault: false,
  active: true,
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface OvertimeSettingsTabProps {
  settings: OvertimeSettings;
  onChange: (settings: OvertimeSettings) => void;
  /** Company ID for API calls — when provided, CRUD operations hit the backend */
  companyId?: string;
  /** Overtime types fetched from the API (managed externally via useOvertimeTypes) */
  types?: OvertimeType[];
  /** Callback to re-fetch types after a mutation */
  onTypesChange?: () => Promise<void>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OvertimeSettingsTab({ settings, onChange, companyId, types, onTypesChange }: OvertimeSettingsTabProps) {
  // If types are provided externally (from the API), use those; otherwise fall back to settings.types
  const externalTypes = types ?? settings.types;

  // Sheet / dialog state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<Omit<OvertimeType, 'id' | 'createdAt'>>(BLANK_TYPE);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [deactivateTarget, setDeactivateTarget] = useState<OvertimeType | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const activeTypes = externalTypes.filter((t) => t.active);
  const inactiveTypes = externalTypes.filter((t) => !t.active);

  function updateSettings(partial: Partial<OvertimeSettings>) {
    onChange({ ...settings, ...partial });
  }

  // ── Enable toggle ──────────────────────────────────────────────────────────

  async function handleToggleEnabled(enabled: boolean) {
    if (enabled && companyId && externalTypes.length === 0) {
      // Seed default types via API on first enable
      setIsSaving(true);
      try {
        for (const seed of SEEDED_OVERTIME_TYPES) {
          await createOvertimeType(companyId, {
            name: seed.name,
            costMultiplier: seed.costMultiplier,
            billMultiplier: seed.billMultiplier,
            badgeColour: seed.badgeColour,
            isDefault: seed.isDefault,
          });
        }
        if (onTypesChange) await onTypesChange();
        toast.success('Default work types created');
      } catch (err) {
        console.error('Failed to seed overtime types:', err);
        toast.error('Failed to create default work types');
      } finally {
        setIsSaving(false);
      }
    } else if (enabled && !companyId && settings.types.length === 0) {
      // Local-only fallback: seed types locally
      onChange({ ...settings, enabled, types: SEEDED_OVERTIME_TYPES });
      return;
    }
    onChange({ ...settings, enabled });
  }

  // ── Type CRUD ──────────────────────────────────────────────────────────────

  function openAddSheet() {
    setEditingId(null);
    setFormValues(BLANK_TYPE);
    setFormErrors({});
    setSheetOpen(true);
  }

  function openEditSheet(type: OvertimeType) {
    setEditingId(type.id);
    setFormValues({
      name: type.name,
      costMultiplier: type.costMultiplier,
      billMultiplier: type.billMultiplier,
      badgeColour: type.badgeColour,
      isDefault: type.isDefault,
      active: type.active,
    });
    setFormErrors({});
    setSheetOpen(true);
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!formValues.name.trim()) errors.name = 'Name is required';

    if (
      formValues.costMultiplier !== undefined &&
      (isNaN(formValues.costMultiplier) || formValues.costMultiplier <= 0)
    ) {
      errors.costMultiplier = 'Must be a positive number';
    }
    if (
      formValues.billMultiplier !== undefined &&
      (isNaN(formValues.billMultiplier) || formValues.billMultiplier <= 0)
    ) {
      errors.billMultiplier = 'Must be a positive number';
    }
    if (formValues.costMultiplier === undefined && formValues.billMultiplier === undefined) {
      errors.costMultiplier = 'At least one multiplier (Cost or Bill) is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSaveType() {
    if (!validateForm()) return;

    if (companyId) {
      // ── API mode ─────────────────────────────────────────────────────────
      setIsSaving(true);
      try {
        if (editingId) {
          const payload: UpdateOvertimeTypePayload = {
            name: formValues.name,
            costMultiplier: formValues.costMultiplier,
            billMultiplier: formValues.billMultiplier,
            badgeColour: formValues.badgeColour,
          };
          await updateOvertimeType(companyId, editingId, payload);

          // If this type is being set as default, call setDefault separately
          if (formValues.isDefault) {
            await setDefaultOvertimeType(companyId, editingId);
          }
        } else {
          const payload: CreateOvertimeTypePayload = {
            name: formValues.name,
            costMultiplier: formValues.costMultiplier,
            billMultiplier: formValues.billMultiplier,
            badgeColour: formValues.badgeColour,
            isDefault: formValues.isDefault,
          };
          await createOvertimeType(companyId, payload);
        }

        if (onTypesChange) await onTypesChange();
        toast.success(editingId ? 'Work type updated' : 'Work type created');
        setSheetOpen(false);
      } catch (err) {
        console.error('Failed to save overtime type:', err);
        toast.error('Failed to save work type', {
          description: err instanceof Error ? err.message : 'Please try again',
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // ── Local-only fallback (demo mode) ──────────────────────────────────
      let updatedTypes: OvertimeType[];

      if (editingId) {
        updatedTypes = externalTypes.map((t) =>
          t.id === editingId
            ? { ...t, ...formValues }
            : formValues.isDefault
            ? { ...t, isDefault: false }
            : t
        );
      } else {
        const newType: OvertimeType = {
          ...formValues,
          id: Date.now(),
          createdAt: new Date(),
        };
        updatedTypes = formValues.isDefault
          ? [...externalTypes.map((t) => ({ ...t, isDefault: false })), newType]
          : [...externalTypes, newType];
      }

      updateSettings({ types: updatedTypes });
      setSheetOpen(false);
    }
  }

  async function handleSetDefault(id: number) {
    if (companyId) {
      setIsSaving(true);
      try {
        await setDefaultOvertimeType(companyId, id);
        if (onTypesChange) await onTypesChange();
      } catch (err) {
        console.error('Failed to set default overtime type:', err);
        toast.error('Failed to set default work type');
      } finally {
        setIsSaving(false);
      }
    } else {
      updateSettings({
        types: externalTypes.map((t) => ({ ...t, isDefault: t.id === id })),
      });
    }
  }

  function handleDeactivate(type: OvertimeType) {
    setDeactivateTarget(type);
  }

  async function confirmDeactivate() {
    if (!deactivateTarget) return;

    if (companyId) {
      setIsSaving(true);
      try {
        await deleteOvertimeType(companyId, deactivateTarget.id);
        if (onTypesChange) await onTypesChange();
        toast.success(`"${deactivateTarget.name}" deactivated`);
      } catch (err) {
        console.error('Failed to deactivate overtime type:', err);
        toast.error('Failed to deactivate work type');
      } finally {
        setIsSaving(false);
      }
    } else {
      updateSettings({
        types: externalTypes.map((t) =>
          t.id === deactivateTarget.id ? { ...t, active: false, isDefault: false } : t
        ),
      });
    }
    setDeactivateTarget(null);
  }

  // Note: handleReactivate is intentionally omitted — no backend endpoint exists.
  // Inactive types display a message directing admins to create a new type instead.

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <Accordion type="multiple" defaultValue={["work-type-tracking", "work-types"]} className="space-y-4">
        {/* ── Section 1: Master toggle ───────────────────────────────────── */}
        <AccordionItem value="work-type-tracking" className="border rounded-lg">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <div className="font-semibold">Work Type Tracking</div>
                <div className="text-sm text-muted-foreground font-normal">
                  Enable work type tracking to allow employees to classify timesheet rows by work type
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="overtime-enabled" className="text-sm font-medium cursor-pointer">
                  Enable Work Type Tracking
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Allow employees to classify timesheet rows by work type
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Checkbox
                  id="overtime-enabled"
                  checked={settings.enabled}
                  onCheckedChange={(v) => !isSaving && handleToggleEnabled(!!v)}
                  disabled={isSaving}
                />
              </div>
            </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {settings.enabled && (
          <>
            {/* ── Section 2: Work types ──────────────────────────────────── */}
            <AccordionItem value="work-types" className="border rounded-lg">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-2">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <Plus className="h-4 w-4 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Work Types</div>
                      <div className="text-sm text-muted-foreground font-normal">
                        Define the work type classifications employees can select when logging time
                      </div>
                    </div>
                  </div>
                  <Button size="sm" className="shrink-0 mr-2" onClick={(e) => { e.stopPropagation(); openAddSheet(); }} disabled={isSaving}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Type
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4 space-y-4">
              {/* Active types table */}
              {activeTypes.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground bg-background">
                  No work types configured yet. Click &quot;Add Type&quot; to create your first one.
                </div>
              ) : (
                <div className="rounded-md border overflow-hidden bg-background">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Name</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Cost ×</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Bill ×</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Default</th>
                        <th className="text-right px-4 py-2 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {activeTypes.map((type) => (
                        <tr key={type.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className="h-3 w-3 rounded-full shrink-0"
                                style={{ backgroundColor: type.badgeColour }}
                              />
                              <span className="font-medium">{type.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {type.costMultiplier !== undefined ? (
                              <Badge variant="secondary">{type.costMultiplier}×</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {type.billMultiplier !== undefined ? (
                              <Badge variant="secondary">{type.billMultiplier}×</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {type.isDefault ? (
                              <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                                Default
                              </Badge>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-muted-foreground hover:text-foreground px-2"
                                onClick={() => handleSetDefault(type.id)}
                                disabled={isSaving}
                              >
                                Set default
                              </Button>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => openEditSheet(type)}
                                title="Edit"
                                disabled={isSaving}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => handleDeactivate(type)}
                                title="Deactivate"
                                disabled={isSaving}
                              >
                                <PowerOff className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Inactive / deactivated types */}
              {inactiveTypes.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground list-none flex items-center gap-2 py-1">
                    <span className="text-xs border border-dashed rounded px-2 py-0.5">
                      {inactiveTypes.length} deactivated type{inactiveTypes.length > 1 ? 's' : ''}
                    </span>
                    <span className="text-xs">(click to show)</span>
                  </summary>
                  <div className="mt-2 rounded-md border border-dashed overflow-hidden">
                    <table className="w-full text-sm opacity-60">
                      <tbody className="divide-y">
                        {inactiveTypes.map((type) => (
                          <tr key={type.id}>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="h-3 w-3 rounded-full shrink-0 grayscale"
                                  style={{ backgroundColor: type.badgeColour }}
                                />
                                <span className="line-through text-muted-foreground">
                                  {type.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span className="text-xs text-muted-foreground">
                                No reactivation available — please create a new type
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </AccordionContent>
            </AccordionItem>
          </>
        )}
      </Accordion>

      {/* ── Add / Edit sheet ──────────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="sm:max-w-[480px] overflow-y-auto">
          <SheetHeader className="px-6 pt-6 pb-4">
            <SheetTitle>{editingId ? 'Edit Work Type' : 'Add Work Type'}</SheetTitle>
            <SheetDescription>
              Configure the name, rate multipliers and display colour for this work type
              classification.
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 space-y-5">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="ot-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ot-name"
                value={formValues.name}
                onChange={(e) => setFormValues((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Time & a Half"
              />
              {formErrors.name && (
                <p className="text-xs text-destructive">{formErrors.name}</p>
              )}
            </div>

            {/* Multipliers */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ot-cost-multiplier">Cost Rate Multiplier</Label>
                <Input
                  id="ot-cost-multiplier"
                  type="number"
                  min={0.01}
                  step={0.05}
                  placeholder="e.g. 1.5"
                  value={formValues.costMultiplier ?? ''}
                  onChange={(e) =>
                    setFormValues((p) => ({
                      ...p,
                      costMultiplier: e.target.value ? parseFloat(e.target.value) : undefined,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Leave blank to keep cost rate unchanged</p>
                {formErrors.costMultiplier && (
                  <p className="text-xs text-destructive">{formErrors.costMultiplier}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ot-bill-multiplier">Bill Rate Multiplier</Label>
                <Input
                  id="ot-bill-multiplier"
                  type="number"
                  min={0.01}
                  step={0.05}
                  placeholder="e.g. 1.25"
                  value={formValues.billMultiplier ?? ''}
                  onChange={(e) =>
                    setFormValues((p) => ({
                      ...p,
                      billMultiplier: e.target.value ? parseFloat(e.target.value) : undefined,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Leave blank to keep bill rate unchanged</p>
                {formErrors.billMultiplier && (
                  <p className="text-xs text-destructive">{formErrors.billMultiplier}</p>
                )}
              </div>
            </div>

            {/* Badge colour */}
            <div className="space-y-2">
              <Label>Badge Colour</Label>
              <div className="flex flex-wrap gap-2">
                {BADGE_COLOURS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    title={c.label}
                    onClick={() => setFormValues((p) => ({ ...p, badgeColour: c.hex }))}
                    className={`h-8 w-8 rounded-full border-2 transition-all ${
                      formValues.badgeColour === c.hex
                        ? 'border-foreground scale-110'
                        : 'border-transparent hover:border-muted-foreground'
                    }`}
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
            </div>

            {/* Is Default */}
            <div className="flex items-center gap-3">
              <Switch
                id="ot-default"
                checked={formValues.isDefault}
                onCheckedChange={(checked) =>
                  setFormValues((p) => ({ ...p, isDefault: checked }))
                }
              />
              <div>
                <Label htmlFor="ot-default" className="cursor-pointer">
                  Set as default
                </Label>
                <p className="text-xs text-muted-foreground">
                  Pre-selected when an employee first assigns a work type to a row
                </p>
              </div>
            </div>
          </div>

          <SheetFooter className="px-6 py-4 border-t mt-6">
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveType} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingId ? 'Save Changes' : 'Add Type'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Deactivate confirmation dialog ────────────────────────────────── */}
      <AlertDialog
        open={!!deactivateTarget}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate &quot;{deactivateTarget?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This type will be hidden from the work type selector. All historical timesheet entries
              that used this type will be preserved and remain unaffected.
              {!companyId && ' You can re-activate it at any time.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeactivate}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
